import { type Config } from 'tailwindcss'

export const extendedTheme = {
	colors: {
		border: 'hsl(var(--border))',
		input: {
			DEFAULT: 'hsl(var(--input))',
			invalid: 'hsl(var(--input-invalid))',
		},
		ring: {
			DEFAULT: 'hsl(var(--ring))',
			invalid: 'hsl(var(--foreground-destructive))',
		},
		background: 'hsl(var(--background))',
		foreground: {
			DEFAULT: 'hsl(var(--foreground))',
			destructive: 'hsl(var(--foreground-destructive))',
		},
		primary: {
			DEFAULT: 'hsl(var(--primary))',
			foreground: 'hsl(var(--primary-foreground))',
		},
		secondary: {
			DEFAULT: 'hsl(var(--secondary))',
			foreground: 'hsl(var(--secondary-foreground))',
		},
		destructive: {
			DEFAULT: 'hsl(var(--destructive))',
			foreground: 'hsl(var(--destructive-foreground))',
		},
		success: {
			DEFAULT: 'hsl(var(--success))',
			foreground: 'hsl(var(--success-foreground))',
		},
		warning: {
			DEFAULT: 'hsl(var(--warning))',
			foreground: 'hsl(var(--warning-foreground))',
		},
		info: {
			DEFAULT: 'hsl(var(--info))',
			foreground: 'hsl(var(--info-foreground))',
		},
		muted: {
			DEFAULT: 'hsl(var(--muted))',
			foreground: 'hsl(var(--muted-foreground))',
		},
		accent: {
			DEFAULT: 'hsl(var(--accent))',
			foreground: 'hsl(var(--accent-foreground))',
		},
		popover: {
			DEFAULT: 'hsl(var(--popover))',
			foreground: 'hsl(var(--popover-foreground))',
		},
		card: {
			DEFAULT: 'hsl(var(--card))',
			foreground: 'hsl(var(--card-foreground))',
		},
		sidebar: {
			DEFAULT: 'hsl(var(--sidebar-background))',
			foreground: 'hsl(var(--sidebar-foreground))',
			primary: 'hsl(var(--sidebar-primary))',
			'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
			accent: 'hsl(var(--sidebar-accent))',
			'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
			border: 'hsl(var(--sidebar-border))',
			ring: 'hsl(var(--sidebar-ring))',
		},

		/* NEW — semantic aliases, resolve to the same colors via --m-* vars */
		ink: 'var(--m-ink)',
		'ink-soft': 'var(--m-ink-soft)',
		'ink-fg': 'var(--m-text)',
		'ink-fg-soft': 'var(--m-text-soft)',
		ember: 'var(--m-ember)',
		'ember-soft': 'var(--m-ember-soft)',
		'm-rule': 'var(--m-rule)',
		cream: 'var(--m-cream)',
		'cream-fg': 'var(--m-cream-text)',
		'cream-fg-soft': 'var(--m-cream-text-soft)',
		'cream-rule': 'var(--m-cream-rule)',
		'cream-ember': 'var(--m-cream-ember)',
	},
	fontFamily: {
		sans: [
			'"Inter Variable"',
			'Inter',
			'system-ui',
			'-apple-system',
			'BlinkMacSystemFont',
			'sans-serif',
		],
		display: [
			'"Source Serif 4 Variable"',
			'"Source Serif 4"',
			'"Iowan Old Style"',
			'Georgia',
			'serif',
		],
		// Ligatures off: this face renders workout notation, where JetBrains
		// Mono's ligatures merge `++`/`--` into single glyphs and hide the
		// actual characters users need to type.
		'mono-display': [
			[
				'"JetBrains Mono Variable"',
				'"JetBrains Mono"',
				'"SF Mono"',
				'Menlo',
				'monospace',
			],
			{ fontFeatureSettings: '"liga" 0, "calt" 0' },
		],
	},
	borderColor: {
		DEFAULT: 'hsl(var(--border))',
	},
	borderRadius: {
		lg: 'var(--radius)',
		md: 'calc(var(--radius) - 2px)',
		sm: 'calc(var(--radius) - 4px)',
	},
	fontSize: {
		/* Display — serif via @layer base rule */
		mega: [
			'clamp(3.2rem, 7vw, 5rem)',
			{ lineHeight: '1.02', letterSpacing: '-0.02em', fontWeight: '600' },
		],
		h1: [
			'clamp(2.4rem, 5vw, 3.6rem)',
			{ lineHeight: '1.05', letterSpacing: '-0.02em', fontWeight: '600' },
		],
		h2: [
			'clamp(1.8rem, 3.5vw, 2.6rem)',
			{ lineHeight: '1.1', letterSpacing: '-0.018em', fontWeight: '600' },
		],
		h3: [
			'clamp(1.4rem, 2.5vw, 1.9rem)',
			{ lineHeight: '1.2', letterSpacing: '-0.015em', fontWeight: '500' },
		],
		h4: [
			'1.25rem',
			{ lineHeight: '1.3', letterSpacing: '-0.01em', fontWeight: '500' },
		],
		h5: ['1.05rem', { lineHeight: '1.4', fontWeight: '500' }],
		h6: ['0.9rem', { lineHeight: '1.45', fontWeight: '600' }],

		/* Body — sans (Inter) */
		'body-2xl': ['1.5rem', { lineHeight: '1.6', fontWeight: '400' }],
		'body-xl': ['1.25rem', { lineHeight: '1.6', fontWeight: '400' }],
		'body-lg': ['1.125rem', { lineHeight: '1.6', fontWeight: '400' }],
		'body-md': ['1rem', { lineHeight: '1.6', fontWeight: '400' }],
		'body-sm': ['0.9rem', { lineHeight: '1.55', fontWeight: '400' }],
		'body-xs': ['0.8rem', { lineHeight: '1.5', fontWeight: '400' }],
		'body-2xs': ['0.7rem', { lineHeight: '1.4', fontWeight: '400' }],

		/* Marketing-style direct names (also useful in-app for explicit display) */
		'display-1': [
			'clamp(2.4rem, 5vw, 3.6rem)',
			{ lineHeight: '1.05', letterSpacing: '-0.02em', fontWeight: '600' },
		],
		'display-2': [
			'clamp(1.8rem, 3.5vw, 2.6rem)',
			{ lineHeight: '1.1', letterSpacing: '-0.018em', fontWeight: '600' },
		],
		'display-3': [
			'clamp(1.4rem, 2.5vw, 1.9rem)',
			{ lineHeight: '1.2', letterSpacing: '-0.015em', fontWeight: '500' },
		],
		eyebrow: [
			'0.72rem',
			{ lineHeight: '1.2', letterSpacing: '0.18em', fontWeight: '500' },
		],
		'mono-label': ['0.78rem', { lineHeight: '1.4', letterSpacing: '0.02em' }],

		caption: ['1rem', { lineHeight: '1.4', fontWeight: '500' }],
		button: [
			'0.78rem',
			{ lineHeight: '1', letterSpacing: '0.02em', fontWeight: '500' },
		],
	},
	keyframes: {
		'caret-blink': {
			'0%,70%,100%': { opacity: '1' },
			'20%,50%': { opacity: '0' },
		},
		'roll-reveal': {
			from: { transform: 'rotate(12deg) scale(0)', opacity: '0' },
			to: { transform: 'rotate(0deg) scale(1)', opacity: '1' },
		},
		'slide-left': {
			from: { transform: 'translateX(20px)', opacity: '0' },
			to: { transform: 'translateX(0px)', opacity: '1' },
		},
		'slide-top': {
			from: { transform: 'translateY(20px)', opacity: '0' },
			to: { transform: 'translateY(0px)', opacity: '1' },
		},
	},
	animation: {
		'caret-blink': 'caret-blink 1.25s ease-out infinite',
		'roll-reveal': 'roll-reveal 0.4s cubic-bezier(.22,1.28,.54,.99)',
		'slide-left': 'slide-left 0.3s ease-out',
		'slide-top': 'slide-top 0.3s ease-out',
	},
} satisfies Config['theme']
